import type { AutomaticColumn, ColumnMeasurementCache, DataSchema } from 'types/table';
import type { ColumnSizingState } from './columnSizing';

export const relativeColumnWidths = {
  Normal: 130,
  Wider: 150,
  Widest: 280,
} as const;

export const getConfiguredAgendaColumnWidth = (item: DataSchema[string] | undefined) => {
  const configuration = item?.formItemDefinition?.tableColumnConfiguration;

  if (configuration?.columnWidthMode === 'Fixed') {
    const width = configuration.fixedColumnWidth;

    return Number.isInteger(width) && width! > 0 ? width : undefined;
  }

  if (configuration?.columnWidthMode === 'Relative') {
    return relativeColumnWidths[configuration.relativeColumnWidth as keyof typeof relativeColumnWidths];
  }

  return undefined;
};

const measureColumns = (table: HTMLTableElement, columns: readonly AutomaticColumn[], cache: ColumnMeasurementCache) => {
  const widths: ColumnSizingState = {};
  const headers: ColumnSizingState = {};
  const ids = new Set(columns.map(({ id }) => id));
  const candidates = Array.from(
    table.querySelectorAll<HTMLElement>(':scope > thead > tr > [data-auto-column], :scope > tbody > tr > [data-auto-column]')
  ).filter(
    (element) => ids.has(element.dataset.autoColumn!) && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden'
  );

  const probes = candidates.map((element) => {
    try {
      const hasContent = Boolean(element.textContent?.trim() || element.querySelector('input, textarea, select'));
      const probe = hasContent ? makeIntrinsicProbe(element) : null;
      const signature = probe?.outerHTML ?? '';
      const cached = cache.get(element);
      return { element, probe, signature, cachedWidth: cached?.signature === signature ? cached.width : undefined };
    } catch {
      return { element, probe: null, signature: '', cachedWidth: undefined };
    }
  });

  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.inert = true;
  container.style.cssText = 'position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;width:max-content;contain:layout style;z-index:-1';
  for (const { probe, cachedWidth } of probes) if (probe && cachedWidth === undefined) container.appendChild(probe);
  if (container.childElementCount) document.body.appendChild(container);
  let measurements: number[];
  try {
    measurements = probes.map(({ probe, cachedWidth }) => {
      try {
        return cachedWidth ?? probe?.getBoundingClientRect().width ?? 0;
      } catch {
        return 0;
      }
    });
  } finally {
    container.remove();
  }

  probes.forEach(({ element, signature }, index) => {
    const width = measurements[index];
    if (!Number.isFinite(width) || width <= 0) return;
    cache.set(element, { signature, width });
    const id = element.dataset.autoColumn!;
    const target = element.closest('thead') ? headers : widths;
    target[id] = Math.max(target[id] ?? 0, width);
  });

  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      Math.ceil(Math.max(headers[column.id] || measureHeaderFallback(table, column), widths[column.id] || 0) - 0.01),
    ])
  );
};

const measureHeaderFallback = (table: HTMLTableElement, column: AutomaticColumn) => {
  const header = Array.from(table.querySelectorAll<HTMLElement>(':scope > thead > tr > [data-auto-column]')).find(
    (element) => element.dataset.autoColumn === column.id
  );
  const label = header?.querySelector<HTMLElement>('.MuiTypography-root') ?? header;
  const text = column.headerText ?? label?.textContent ?? column.id;
  const measurementElement = document.createElement('span');
  let fontSize = 14;
  let padding = 16;
  try {
    const typography = getComputedStyle(label ?? table);
    const cellStyle = getComputedStyle(header ?? table);
    fontSize = Number.parseFloat(typography.fontSize) || fontSize;
    padding = (Number.parseFloat(cellStyle.paddingLeft) || 0) + (Number.parseFloat(cellStyle.paddingRight) || 0) || padding;
    measurementElement.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;display:inline-block;width:max-content;max-width:none;white-space:nowrap';
    for (const property of intrinsicStyleProperties) measurementElement.style.setProperty(property, typography.getPropertyValue(property));
    measurementElement.style.display = 'inline-block';
    measurementElement.style.padding = '0';
    measurementElement.style.margin = '0';
    measurementElement.style.border = '0';
    measurementElement.textContent = text;
    measurementElement.setAttribute('aria-hidden', 'true');
    document.body.appendChild(measurementElement);
    const width = measurementElement.getBoundingClientRect().width;
    if (Number.isFinite(width) && width > 0) return width + padding;
    const context = document.createElement('canvas').getContext('2d');
    if (context) {
      context.font = typography.font || `${fontSize}px sans-serif`;
      const textWidth = context.measureText(text).width;
      if (Number.isFinite(textWidth) && textWidth > 0) return textWidth + padding;
    }
  } catch {
    /* empty */
  } finally {
    measurementElement.remove();
  }

  return Math.max(1, Array.from(text).length * fontSize + padding);
};

export const measureAutomaticColumns = (table: HTMLTableElement, columns: readonly AutomaticColumn[], cache: ColumnMeasurementCache) => {
  try {
    return measureColumns(table, columns, cache);
  } catch {
    return Object.fromEntries(columns.map((column) => [column.id, Math.ceil(measureHeaderFallback(table, column))]));
  }
};

const intrinsicStyleProperties = [
  'display',
  'box-sizing',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-stretch',
  'font-variant',
  'font-feature-settings',
  'font-variation-settings',
  'letter-spacing',
  'word-spacing',
  'text-transform',
  'text-indent',
  'direction',
  'padding-left',
  'padding-right',
  'margin-left',
  'margin-right',
  'border-left-width',
  'border-right-width',
  'border-left-style',
  'border-right-style',
  'gap',
  'column-gap',
  'flex-direction',
  'align-items',
  'vertical-align',
] as const;

const makeIntrinsicProbe = (source: HTMLElement): HTMLElement => {
  const probe = source.cloneNode(true) as HTMLElement;
  const sources = [source, ...source.querySelectorAll<HTMLElement | SVGElement>('*')];
  const clones = [probe, ...probe.querySelectorAll<HTMLElement | SVGElement>('*')];
  sources.forEach((element, index) => {
    const clone = clones[index];
    const computed = getComputedStyle(element);
    const preserveDimensions = element instanceof SVGElement || element instanceof HTMLImageElement;
    clone.removeAttribute('id');
    clone.removeAttribute('name');
    delete clone.dataset.autoColumn;
    for (const property of intrinsicStyleProperties) clone.style.setProperty(property, computed.getPropertyValue(property), 'important');
    clone.style.setProperty('width', preserveDimensions ? computed.width : 'max-content', 'important');
    clone.style.setProperty('height', preserveDimensions ? computed.height : 'auto', 'important');
    if (computed.display === 'block' || computed.display === 'table-cell') clone.style.setProperty('display', 'inline-block', 'important');
    for (const [property, value] of Object.entries({
      'min-width': '0',
      'max-width': 'none',
      'white-space': 'nowrap',
      overflow: 'visible',
      'text-overflow': 'clip',
      flex: '0 0 auto',
      'flex-wrap': 'nowrap',
      '-webkit-line-clamp': 'unset',
      position: 'static',
      transform: 'none',
      transition: 'none',
      animation: 'none',
    }))
      clone.style.setProperty(property, value, 'important');
    if (computed.display === 'none' || computed.position === 'absolute' || computed.position === 'fixed') {
      clone.style.setProperty('display', 'none', 'important');
    }
    if (element.tagName === 'BR') clone.replaceWith(document.createTextNode(' '));
    if ((element instanceof HTMLInputElement && !['checkbox', 'radio', 'button'].includes(element.type)) || element instanceof HTMLTextAreaElement) {
      const text = document.createElement('span');
      text.style.cssText = clone.style.cssText;
      text.textContent = element.value || element.placeholder;
      clone.replaceWith(text);
    }
    if (element instanceof HTMLSelectElement) {
      const text = document.createElement('span');
      text.style.cssText = clone.style.cssText;
      text.textContent = Array.from(element.selectedOptions, (option) => option.label).join('; ');
      clone.replaceWith(text);
    }
  });
  return probe;
};
